import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ordersApi, type Order } from "../../services/ordersApi";
import { queryClient } from "@/shared/services/queryClient";
import { useAuthStore } from "@/features/auth/stores/authStore";

export function ReviewPaymentOverrideDialog({ order, open, onOpenChange }: { order: Order; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { hasPermission } = useAuthStore();
  const canApprove = hasPermission("orders.approve_payment_threshold_override");
  const canRevoke = hasPermission("orders.revoke_payment_threshold_override");
  const [notes, setNotes] = useState("");
  const [revokeReason, setRevokeReason] = useState("");
  const query = useQuery({
    queryKey: ["orders", order.id, "payment-threshold-overrides"],
    queryFn: () => ordersApi.getPaymentThresholdOverrides(order.id),
    enabled: open,
  });
  const pending = query.data?.find((item) => item.status === "Pending");
  const approved = query.data?.find((item) => item.status === "Approved");
  const latest = query.data?.[0];

  const modeTitle = pending && canApprove
    ? "Review Override Request"
    : approved
      ? "View Approved Override"
      : "View Override Status";

  const decision = useMutation({
    mutationFn: (value: "approve" | "reject") => ordersApi.decidePaymentThresholdOverride(pending!.id, value, notes),
    onSuccess: (result) => {
      toast.success(`Override ${result.status.toLowerCase()}`);
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", order.id, "payment-threshold-overrides"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "payment-threshold-overrides", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["orders", order.id, "work-authorization-status"] });
      onOpenChange(false);
    },
    onError: (error: unknown) => toast.error(error instanceof Error ? error.message : "Could not decide override"),
  });

  const revoke = useMutation({
    mutationFn: () => ordersApi.revokePaymentThresholdOverride(approved!.id, revokeReason.trim()),
    onSuccess: () => {
      toast.success("Override revoked");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", order.id, "payment-threshold-overrides"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "payment-threshold-overrides", "queue"] });
      queryClient.invalidateQueries({ queryKey: ["orders", order.id, "work-authorization-status"] });
      onOpenChange(false);
    },
    onError: (error: unknown) => toast.error(error instanceof Error ? error.message : "Could not revoke override"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{modeTitle}</DialogTitle>
          <DialogDescription>
            Order {order.orderNumber}. Payment collection and work authorization are separate controls.
          </DialogDescription>
        </DialogHeader>

        {query.isLoading ? <p className="text-sm text-muted-foreground">Loading request…</p> : pending ? (
          <div className="space-y-4">
            <div className="rounded-md border p-3 text-sm space-y-1">
              <p><strong>Status:</strong> Pending review</p>
              <p><strong>Reason:</strong> {pending.reason}</p>
              <p><strong>Required:</strong> {pending.requiredThresholdPercentage}% ({pending.requiredAmount.toLocaleString()} KES)</p>
              <p><strong>Actually paid:</strong> {pending.actualPaidPercentage.toFixed(1)}% ({pending.paidAmount.toLocaleString()} KES)</p>
              <p><strong>Submitted:</strong> {new Date(pending.requestedAt).toLocaleString()}</p>
            </div>
            {canApprove ? (
              <>
                <Textarea placeholder="Approval or rejection notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
                <div className="flex justify-end gap-2">
                  <Button variant="destructive" disabled={decision.isPending} onClick={() => decision.mutate("reject")}>Reject</Button>
                  <Button disabled={decision.isPending} onClick={() => decision.mutate("approve")}>Approve override</Button>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                  Awaiting approver decision. Review happens in Operations {">"} Payment Threshold Overrides.
              </p>
            )}
          </div>
        ) : approved ? (
          <div className="space-y-4">
            <div className="rounded-md border p-3 text-sm space-y-1">
              <p><strong>Status:</strong> Approved</p>
              <p><strong>Reason:</strong> {approved.reason}</p>
              <p><strong>Required:</strong> {approved.requiredThresholdPercentage}% ({approved.requiredAmount.toLocaleString()} KES)</p>
              <p><strong>Actually paid:</strong> {approved.actualPaidPercentage.toFixed(1)}% ({approved.paidAmount.toLocaleString()} KES)</p>
              <p><strong>Approved at:</strong> {approved.approvedAt ? new Date(approved.approvedAt).toLocaleString() : "-"}</p>
            </div>
            {canRevoke && (
              <>
                <Textarea
                  placeholder="Reason for revoking this approved override"
                  value={revokeReason}
                  onChange={(event) => setRevokeReason(event.target.value)}
                />
                <div className="flex justify-end">
                  <Button
                    variant="destructive"
                    disabled={revoke.isPending || !revokeReason.trim()}
                    onClick={() => revoke.mutate()}
                  >
                    Revoke override
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : latest ? (
          <div className="rounded-md border p-3 text-sm space-y-1">
            <p><strong>Status:</strong> {latest.status}</p>
            <p><strong>Reason:</strong> {latest.reason}</p>
            <p><strong>Submitted:</strong> {new Date(latest.requestedAt).toLocaleString()}</p>
            {latest.expiresAt && <p><strong>Expires:</strong> {new Date(latest.expiresAt).toLocaleString()}</p>}
            <p className="text-muted-foreground pt-2">
              Requesters can submit a new request when the order still fails payment threshold checks.
            </p>
          </div>
        ) : <p className="text-sm text-muted-foreground">No override requests recorded for this order.</p>}
      </DialogContent>
    </Dialog>
  );
}
